/**
 * A person's photo, or their initials in a tinted circle.
 *
 * The tint rotates through the same green/blue/orange triplet already used
 * for the dashboard's stat medallions (`.stat:nth-child(3n+2/3n+3)` in
 * styles.css) — a person without a photo on file still gets a mark that
 * reads as "this app's own," not a generic gray circle. The rotation is
 * keyed off the name (a stable hash), not position, so the same person's
 * initials are always the same color wherever they appear.
 */

const TINTS = ['avatar--accent', 'avatar--info', 'avatar--highlight'] as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

function tintClass(name: string): (typeof TINTS)[number] {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return TINTS[hash % TINTS.length]!;
}

export function Avatar({
  name,
  photoUrl,
  size = 'sm',
}: {
  name: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const className = `avatar avatar--${size}`;
  return photoUrl ? (
    <img className={className} src={photoUrl} alt="" />
  ) : (
    <span className={`${className} ${tintClass(name)}`} aria-hidden="true">
      {initials(name)}
    </span>
  );
}
